import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import monitoringApi from '../../services/monitoringApi';
import { useAuth } from '../../context/AuthContext';
import { scanQueries, vendorQueries } from '../../lib/queries';
import { T, sans, card, topBtn } from './VendorDetails';

/* ── model ─────────────────────────────────────────────────────────── */
export type Frequency = 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'custom';
export type Depth = 'fast' | 'deep';

interface Schedule {
  frequency: Frequency;
  day_of_week: number | null;
  day_of_month: number | null;
  interval_days: number | null;
  time: string;
  is_active: number;
  scan_type: Depth;
  last_run: string | null;
  next_run: string | null;
  updated_at?: string | null;
  inherits_default?: boolean;
}

interface Overview { schedule: Schedule | null; upcoming_runs: string[] }

export interface Draft {
  frequency: Frequency;
  day_of_week: number;      // ISO: 1 = Monday ... 7 = Sunday
  day_of_month: number;     // 1-28
  interval_days: number;
  time: string;
  is_active: number;
  scan_type: Depth;
}

interface Props {
  vendorId: string;
  vendor: { name: string; domain: string; riskLevel?: string; score?: number };
  isDark: boolean;
}

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const CADENCES: { value: Frequency; label: string; hint: string }[] = [
  { value: 'daily', label: 'Daily', hint: 'Every day' },
  { value: 'weekly', label: 'Weekly', hint: 'Once a week' },
  { value: 'monthly', label: 'Monthly', hint: 'Once a month' },
  { value: 'quarterly', label: 'Quarterly', hint: 'Every 3 months' },
  { value: 'custom', label: 'Custom', hint: 'Every N days' },
];
export const DEPTHS: { value: Depth; label: string; hint: string }[] = [
  { value: 'fast', label: 'Fast scan', hint: 'Primary domain only. Finishes in a couple of minutes.' },
  { value: 'deep', label: 'Deep scan', hint: 'Every discovered subdomain. Thorough, takes longer.' },
];

// Rough length of one cycle, used to compare cadences and to judge whether the last scan is still fresh.
export const cycleDays = (s: { frequency: Frequency; interval_days?: number | null }) =>
  ({ daily: 1, weekly: 7, monthly: 30, quarterly: 91, custom: Math.max(1, s.interval_days || 1) } as Record<Frequency, number>)[s.frequency];

export const describe = (s: { frequency: Frequency; day_of_week?: number | null; day_of_month?: number | null; interval_days?: number | null; time: string }) => {
  const time = fmtClock(s.time);
  switch (s.frequency) {
    case 'daily': return `Every day at ${time}`;
    case 'weekly': return `Every ${DAYS[((s.day_of_week || 1) - 1 + 7) % 7]} at ${time}`;
    case 'monthly': return `Monthly on the ${ordinal(s.day_of_month || 1)} at ${time}`;
    case 'quarterly': return `Quarterly on the ${ordinal(s.day_of_month || 1)} at ${time}`;
    case 'custom': {
      const n = s.interval_days || 1;
      return n % 7 === 0 && n > 7 ? `Every ${n / 7} weeks at ${time}` : `Every ${n} day${n === 1 ? '' : 's'} at ${time}`;
    }
  }
};

/* Recommended cadence from what this vendor is and what it holds -- deterministic, no AI. The strictest
   reason wins. */
const SENSITIVITY_LABEL: Record<string, string> = {
  regulated: 'regulated data', financial: 'financial data', pii: 'personal data', internal: 'internal data',
};
export function recommend(riskLevel?: string, sensitivity?: string | null) {
  const risk = (riskLevel || '').toLowerCase();
  const rules: { days: number; draft: Partial<Draft>; label: string; reason: string | null }[] = [
    { days: 7, draft: { frequency: 'weekly' }, label: 'Weekly',
      reason: risk === 'critical' ? 'Critical risk' : sensitivity === 'regulated' || sensitivity === 'financial' ? `Handles ${SENSITIVITY_LABEL[sensitivity]}` : null },
    { days: 14, draft: { frequency: 'custom', interval_days: 14 }, label: 'Every 2 weeks',
      reason: risk === 'high' ? 'High risk' : sensitivity === 'pii' ? 'Handles personal data' : null },
    { days: 30, draft: { frequency: 'monthly' }, label: 'Monthly',
      reason: risk === 'medium' ? 'Medium risk' : sensitivity === 'internal' ? 'Handles internal data' : null },
  ];
  const hit = rules.find(r => r.reason);
  return hit
    ? { days: hit.days, draft: hit.draft, label: hit.label, reason: hit.reason as string }
    : { days: 91, draft: { frequency: 'quarterly' as Frequency }, label: 'Quarterly', reason: risk === 'low' ? 'Low risk' : 'No elevated risk recorded' };
}

/* ── formatting ────────────────────────────────────────────────────── */
const ordinal = (n: number) => `${n}${['th', 'st', 'nd', 'rd'][(n % 100 - 20) % 10] || ['th', 'st', 'nd', 'rd'][n % 100] || 'th'}`;
export const fmtClock = (hhmm: string) => {
  const [h, m] = (hhmm || '09:00').split(':').map(Number);
  return new Date(2000, 0, 1, h, m).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};
const fmtDay = (iso: string) => new Date(iso).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
export const fmtFull = (iso: string) => new Date(iso).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
export const relative = (iso: string) => {
  const ms = new Date(iso).getTime() - Date.now();
  const mins = Math.round(Math.abs(ms) / 60000);
  const text = mins < 1 ? 'now' : mins < 60 ? `${mins} min` : mins < 1440 ? `${Math.round(mins / 60)} hr` : `${Math.round(mins / 1440)} day${Math.round(mins / 1440) === 1 ? '' : 's'}`;
  if (text === 'now') return 'just now';
  return ms >= 0 ? `in ${text}` : `${text} ago`;
};
const scoreColor = (s: number) => (s >= 80 ? '#16A34A' : s >= 60 ? '#D97706' : '#DC2626');
const apiError = (e: any, fallback: string) => {
  const d = e?.response?.data?.detail;
  return typeof d === 'string' ? d : (d && typeof d === 'object' && typeof d.message === 'string') ? d.message : fallback;
};

/* ── icons ─────────────────────────────────────────────────────────── */
export const Icon = ({ d, size = 14 }: { d: string; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d={d} /></svg>
);
export const I = {
  clock: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20ZM12 6v6l4 2',
  play: 'M6 4l14 8-14 8V4Z',
  pause: 'M6 4h4v16H6zM14 4h4v16h-4z',
  edit: 'M12 20h9M16.5 3.5a2.1 2.1 0 1 1 3 3L7 19l-4 1 1-4Z',
  close: 'M6 6l12 12M18 6 6 18',
  check: 'M5 12l5 5L20 7',
  bolt: 'M13 2 3 14h9l-1 8 10-12h-9l1-8Z',
  layers: 'M12 2 2 7l10 5 10-5-10-5ZM2 17l10 5 10-5M2 12l10 5 10-5',
  chevron: 'M6 9l6 6 6-6',
  sparkle: 'M12 3v4M12 17v4M3 12h4M17 12h4M5.6 5.6l2.8 2.8M15.6 15.6l2.8 2.8M5.6 18.4l2.8-2.8M15.6 8.4l2.8-2.8',
  trash: 'M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6',
  arrow: 'M9 6l6 6-6 6',
};

/* ═════════════════════════════════════════════════════════════════════ */
export default function VendorSchedulePage({ vendorId, vendor, isDark }: Props) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canEdit = !!user && user.role !== 'viewer';

  const c = {
    ink: isDark ? '#e5e7eb' : T.ink, slate: isDark ? '#94a3b8' : T.slate, muted: isDark ? '#64748b' : T.muted,
    line: isDark ? '#1f1f1f' : T.bdr, surface: isDark ? '#0d0d0d' : T.surface, surface2: isDark ? '#141414' : T.surface2,
    accent: isDark ? '#3B82F6' : T.blue, accentBg: isDark ? '#0b1730' : T.blueBg,
  };

  const overviewQuery = useQuery({
    queryKey: ['monitoring', 'schedule', 'vendor', vendorId, 'overview'],
    queryFn: async () => (await api.get(`/v1/monitoring/schedule/vendor/${vendorId}/overview`)).data as Overview,
  });
  const scansQuery = useQuery({ ...scanQueries.byDomain(vendor.domain), enabled: !!vendor.domain });
  const profileQuery = useQuery(vendorQueries.profile(vendorId));

  const globalQuery = useQuery({
    queryKey: ['monitoring', 'schedule', 'global'],
    queryFn: async () => (await monitoringApi.getGlobalSchedule()).data as Schedule & { is_active: number },
  });
  const defaultSchedule = globalQuery.data ?? null;
  const schedule = overviewQuery.data?.schedule ?? null;
  const followsDefault = !!schedule?.inherits_default;
  const upcoming = overviewQuery.data?.upcoming_runs ?? [];
  const scans = useMemo(() => (scansQuery.data ?? []).filter((s: any) => s.completed_at || s.overall_score != null), [scansQuery.data]);
  const sensitivity: string | null = profileQuery.data?.data_sensitivity ?? null;
  const rec = recommend(vendor.riskLevel, sensitivity);
  const history = useMemo(() => ((profileQuery.data?.timeline ?? []) as any[])
    .filter(e => (e.action || '').startsWith('schedule.')).slice(0, 6), [profileQuery.data]);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editorSeed, setEditorSeed] = useState<Partial<Draft> | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [runMenu, setRunMenu] = useState(false);
  const [toast, setToast] = useState<{ text: string; error?: boolean; link?: string } | null>(null);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 6000);
    return () => clearTimeout(t);
  }, [toast]);

  const save = useMutation({
    mutationFn: (draft: Draft) => monitoringApi.createVendorSchedule({ vendor_id: vendorId, ...draft }),
    meta: { invalidates: [['monitoring'], ['vendors', 'detail', vendorId]] },
  });
  const remove = useMutation({
    mutationFn: () => monitoringApi.deleteVendorSchedule(vendorId),
    meta: { invalidates: [['monitoring'], ['vendors', 'detail', vendorId]] },
  });
  const runNow = useMutation({
    mutationFn: async (depth: Depth) => (await api.post(`/v1/vendors/${vendorId}/trigger-scan`, null, { params: { scan_type: depth } })).data,
    meta: { invalidates: [['scans'], ['vendors']] },
  });

  const toDraft = (s: Schedule | null, overrides: Partial<Draft> = {}): Draft => ({
    frequency: s?.frequency || 'weekly',
    day_of_week: s?.day_of_week && s.day_of_week >= 1 && s.day_of_week <= 7 ? s.day_of_week : 1,
    day_of_month: Math.min(Math.max(s?.day_of_month || 1, 1), 28),
    interval_days: s?.interval_days || 14,
    time: s?.time || '09:00',
    is_active: s ? s.is_active : 1,
    scan_type: s?.scan_type || 'fast',
    ...overrides,
  });

  const openEditor = (seed: Partial<Draft> | null = null) => { setEditorSeed(seed); setEditorOpen(true); };

  const togglePause = async () => {
    if (!schedule) return;
    try {
      await save.mutateAsync({ ...toDraft(schedule, { is_active: schedule.is_active ? 0 : 1 }), follow_default: followsDefault } as Draft);
      setToast({ text: schedule.is_active ? 'Automatic scans paused. Settings are kept.' : 'Automatic scans resumed.' });
    } catch (e) { setToast({ text: apiError(e, 'Could not update the schedule.'), error: true }); }
  };

  const triggerScan = async (depth: Depth) => {
    setRunMenu(false);
    try {
      const data = await runNow.mutateAsync(depth);
      setToast({ text: `${depth === 'deep' ? 'Deep' : 'Fast'} scan started for ${vendor.name}.`, link: data?.scan_id ? `/scans/${data.scan_id}/progress` : undefined });
    } catch (e) { setToast({ text: apiError(e, 'Could not start the scan.'), error: true }); }
  };

  const doRemove = async () => {
    try {
      await remove.mutateAsync();
      setConfirmRemove(false);
      setToast({ text: defaultSchedule?.is_active
        ? `${vendor.name} now follows your default schedule.`
        : 'Own schedule removed. Your default schedule is off, so this vendor is only scanned when someone runs a scan.' });
    } catch (e) { setToast({ text: apiError(e, 'Could not remove the schedule.'), error: true }); }
  };

  /* freshness: is the newest completed scan within the expected cycle? */
  const lastScan = scans[0];
  const lastScanAt = lastScan ? (lastScan.completed_at || lastScan.created_at) : null;
  const expectDays = schedule ? cycleDays(schedule) : rec.days;
  const ageDays = lastScanAt ? (Date.now() - new Date(lastScanAt).getTime()) / 86400000 : null;
  const freshness = ageDays == null
    ? { label: 'Never scanned', color: '#DC2626', note: 'No completed scan on record yet.' }
    : ageDays <= expectDays
      ? { label: 'Up to date', color: '#16A34A', note: `Within its ${schedule ? 'scheduled' : 'recommended'} ${expectDays}-day cycle.` }
      : ageDays <= expectDays * 1.5
        ? { label: 'Due soon', color: '#D97706', note: `Slightly past its ${expectDays}-day cycle.` }
        : { label: 'Overdue', color: '#DC2626', note: `${Math.floor(ageDays - expectDays)} days past its ${expectDays}-day cycle.` };

  const looserThanRecommended = !!schedule && cycleDays(schedule) > rec.days;

  /* ── styles ──────────────────────────────────────────────────────── */
  const pill = (color: string): React.CSSProperties => ({
    display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, padding: '4px 10px', borderRadius: 99,
    color, background: `${color}${isDark ? '22' : '14'}`, border: `1px solid ${color}40`, whiteSpace: 'nowrap',
  });
  const dot = (color: string): React.CSSProperties => ({ width: 7, height: 7, borderRadius: 99, background: color, flexShrink: 0 });
  const sectionTitle: React.CSSProperties = { fontSize: 14.5, fontWeight: 700, color: c.ink };
  const sectionSub: React.CSSProperties = { fontSize: 12.5, color: c.muted, marginTop: 2 };

  if (overviewQuery.isPending) {
    return (
      <div style={{ fontFamily: sans, display: 'grid', gap: 14 }}>
        <style>{'@keyframes vsShimmer { 0%,100% { opacity: .55 } 50% { opacity: 1 } }'}</style>
        <div style={{ ...card(isDark), height: 190, animation: 'vsShimmer 1.2s infinite' }} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div style={{ ...card(isDark), height: 260, animation: 'vsShimmer 1.2s infinite' }} />
          <div style={{ ...card(isDark), height: 260, animation: 'vsShimmer 1.2s infinite' }} />
        </div>
      </div>
    );
  }
  if (overviewQuery.isError) {
    return (
      <div style={{ ...card(isDark), padding: 36, textAlign: 'center', fontFamily: sans }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: c.ink, marginBottom: 6 }}>Couldn't load this vendor's schedule</div>
        <button onClick={() => overviewQuery.refetch()} style={{ ...topBtn(false, isDark), margin: '12px auto 0' }}>Try again</button>
      </div>
    );
  }

  const status = !schedule ? { label: 'Not scheduled', color: '#64748B' } : schedule.is_active ? { label: 'Active', color: '#16A34A' } : { label: 'Paused', color: '#D97706' };

  return (
    <div style={{ fontFamily: sans }}>
      <style>{'@keyframes vsSlide { from { transform: translateX(16px); opacity: 0 } to { transform: none; opacity: 1 } } @keyframes vsPop { from { transform: translateY(6px) scale(.98); opacity: 0 } to { transform: none; opacity: 1 } } @keyframes vsToast { from { transform: translateY(8px); opacity: 0 } to { transform: none; opacity: 1 } }'}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: c.ink, letterSpacing: '-.01em' }}>Scan schedule</div>
          <div style={{ fontSize: 13.5, color: c.slate, marginTop: 4, maxWidth: 620, lineHeight: 1.5 }}>
            When {vendor.name} is re-scanned automatically, how deep each scan goes, and how current its risk score is.
          </div>
        </div>
        {canEdit && (
          <div style={{ display: 'flex', gap: 8, position: 'relative' }}>
            <div style={{ position: 'relative' }}>
              <button onClick={() => setRunMenu(o => !o)} disabled={runNow.isPending} style={{ ...topBtn(false, isDark), opacity: runNow.isPending ? 0.6 : 1 }}>
                <Icon d={I.play} size={12} /> {runNow.isPending ? 'Starting…' : 'Run scan now'} <Icon d={I.chevron} size={12} />
              </button>
              {runMenu && (
                <>
                  <div onClick={() => setRunMenu(false)} style={{ position: 'fixed', inset: 0, zIndex: 19 }} />
                  <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 6px)', zIndex: 20, width: 280, padding: 6, background: c.surface, border: `1px solid ${c.line}`, borderRadius: 10 }}>
                    {DEPTHS.map(d => (
                      <button key={d.value} onClick={() => triggerScan(d.value)} style={{ display: 'flex', gap: 10, width: '100%', textAlign: 'left', padding: '10px 12px', borderRadius: 7, border: 'none', background: 'none', cursor: 'pointer', fontFamily: sans }}
                        onMouseEnter={e => (e.currentTarget.style.background = c.surface2)} onMouseLeave={e => (e.currentTarget.style.background = 'none')}>
                        <span style={{ color: c.accent, marginTop: 1 }}><Icon d={d.value === 'deep' ? I.layers : I.bolt} /></span>
                        <span><span style={{ display: 'block', fontSize: 13.5, fontWeight: 600, color: c.ink }}>{d.label}</span><span style={{ display: 'block', fontSize: 12, color: c.muted, marginTop: 2 }}>{d.hint}</span></span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
            {schedule && (
              <button onClick={() => openEditor()} style={topBtn(true, isDark)}><Icon d={I.edit} size={13} /> Edit schedule</button>
            )}
          </div>
        )}
      </div>

      {/* Hero */}
      {schedule ? (
        <div style={{ ...card(isDark), padding: 0, overflow: 'hidden', marginBottom: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.5fr) minmax(0, 1fr)' }}>
            <div style={{ padding: '22px 24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={pill(status.color)}><span style={dot(status.color)} />{status.label}</span>
                <span style={pill(c.accent)}><Icon d={schedule.scan_type === 'deep' ? I.layers : I.bolt} size={12} />{schedule.scan_type === 'deep' ? 'Deep scan' : 'Fast scan'}</span>
                <span style={pill(followsDefault ? '#64748B' : '#7C3AED')}>{followsDefault ? 'Follows your default schedule' : 'Own schedule'}</span>
              </div>
              <div style={{ fontSize: 26, fontWeight: 700, color: c.ink, marginTop: 14, letterSpacing: '-.01em' }}>{describe(schedule)}</div>
              {schedule.is_active && upcoming[0] ? (
                <div style={{ marginTop: 10, display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 15, fontWeight: 600, color: c.ink }}>Next scan {relative(upcoming[0])}</span>
                  <span style={{ fontSize: 13.5, color: c.muted }}>{fmtFull(upcoming[0])}</span>
                </div>
              ) : (
                <div style={{ marginTop: 10, fontSize: 14, color: '#D97706' }}>Paused. Nothing runs until it's resumed; the settings are kept.</div>
              )}
              {canEdit && (
                <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
                  <button onClick={togglePause} disabled={save.isPending} style={{ ...topBtn(false, isDark), opacity: save.isPending ? 0.6 : 1 }}>
                    <Icon d={schedule.is_active ? I.pause : I.play} size={12} /> {schedule.is_active ? 'Pause' : 'Resume'}
                  </button>
                  {!followsDefault && (
                    <button onClick={() => setConfirmRemove(true)} style={topBtn(false, isDark)}>
                      <Icon d={I.trash} size={13} /> {defaultSchedule?.is_active ? 'Use default schedule' : 'Remove schedule'}
                    </button>
                  )}
                </div>
              )}
            </div>
            <div style={{ padding: '22px 24px', borderLeft: `1px solid ${c.line}`, background: c.surface2, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 14 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: c.slate, textTransform: 'uppercase', letterSpacing: '.05em' }}>Data freshness</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                  <span style={dot(freshness.color)} />
                  <span style={{ fontSize: 18, fontWeight: 700, color: freshness.color }}>{freshness.label}</span>
                </div>
                <div style={{ fontSize: 13, color: c.slate, marginTop: 4 }}>{freshness.note}</div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <Stat label="Last scanned" value={lastScanAt ? relative(lastScanAt) : '—'} hint={lastScanAt ? fmtDay(lastScanAt) : undefined} c={c} />
                <Stat label="Last scheduled run" value={schedule.last_run ? relative(schedule.last_run) : 'Not yet'} hint={schedule.last_run ? fmtDay(schedule.last_run) : undefined} c={c} />
              </div>
            </div>
          </div>
          {looserThanRecommended && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', padding: '12px 24px', borderTop: `1px solid ${c.line}`, background: isDark ? '#1f1503' : T.ambBg }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13.5, color: isDark ? '#fbbf24' : T.amb }}>
                <Icon d={I.sparkle} /> <span><strong>{rec.reason}</strong> — we recommend scanning at least {rec.label.toLowerCase()}.</span>
              </span>
              {canEdit && <button onClick={() => openEditor(rec.draft)} style={{ ...topBtn(false, isDark), padding: '6px 12px', fontSize: 12.5 }}>Switch to {rec.label.toLowerCase()}</button>}
            </div>
          )}
        </div>
      ) : (
        <div style={{ ...card(isDark), padding: '28px 28px', marginBottom: 14, display: 'grid', gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 1fr)', gap: 28, alignItems: 'center' }}>
          <div>
            <span style={pill('#64748B')}><span style={dot('#64748B')} />Not scheduled</span>
            <div style={{ fontSize: 22, fontWeight: 700, color: c.ink, marginTop: 14 }}>{vendor.name} isn't re-scanned automatically</div>
            <div style={{ fontSize: 14, color: c.slate, marginTop: 6, lineHeight: 1.55, maxWidth: 520 }}>
              Your default schedule is switched off, so its risk score only changes when someone runs a scan by hand.
              Set a schedule for this vendor, or turn the default back on from the Schedule page.
            </div>
            {canEdit && (
              <div style={{ display: 'flex', gap: 8, marginTop: 18, flexWrap: 'wrap' }}>
                <button onClick={() => openEditor(rec.draft)} style={topBtn(true, isDark)}><Icon d={I.sparkle} /> Scan {rec.label.toLowerCase()} (recommended)</button>
                <button onClick={() => openEditor()} style={topBtn(false, isDark)}>Choose another schedule</button>
              </div>
            )}
          </div>
          <div style={{ padding: '16px 18px', borderRadius: 12, background: c.accentBg, border: `1px solid ${isDark ? '#1e3a8a' : T.blueBdr}` }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: c.accent, textTransform: 'uppercase', letterSpacing: '.05em' }}>Recommended for this vendor</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: c.ink, marginTop: 6 }}>{rec.label}</div>
            <div style={{ fontSize: 13, color: c.slate, marginTop: 4 }}>{rec.reason}</div>
            <div style={{ height: 1, background: isDark ? '#1e3a8a' : T.blueBdr, margin: '12px 0' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={dot(freshness.color)} />
              <span style={{ fontSize: 13.5, color: c.ink, fontWeight: 600 }}>{freshness.label}</span>
              <span style={{ fontSize: 13, color: c.muted }}>{lastScanAt ? `· last scanned ${relative(lastScanAt)}` : ''}</span>
            </div>
          </div>
        </div>
      )}

      {/* Upcoming + activity */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: 14, marginBottom: 14 }}>
        <div style={{ ...card(isDark), padding: '18px 20px' }}>
          <div style={sectionTitle}>Upcoming scans</div>
          <div style={sectionSub}>Calculated the same way the scheduler runs them.</div>
          {!schedule || !schedule.is_active || upcoming.length === 0 ? (
            <div style={{ padding: '30px 8px', textAlign: 'center', color: c.muted, fontSize: 13.5 }}>
              {!schedule ? 'No automatic scans planned.' : 'Paused — nothing is planned until it\'s resumed.'}
            </div>
          ) : (
            <div style={{ marginTop: 16, position: 'relative' }}>
              <div style={{ position: 'absolute', left: 21, top: 18, bottom: 18, width: 2, background: c.line }} />
              {upcoming.map((iso, i) => {
                const d = new Date(iso);
                return (
                  <div key={iso} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '8px 0', position: 'relative' }}>
                    <div style={{
                      width: 44, height: 44, borderRadius: 11, flexShrink: 0, zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                      background: i === 0 ? c.accent : c.surface, border: `1px solid ${i === 0 ? c.accent : c.line}`, color: i === 0 ? '#fff' : c.ink,
                    }}>
                      <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', opacity: 0.85 }}>{d.toLocaleDateString('en-US', { month: 'short' })}</span>
                      <span style={{ fontSize: 16, fontWeight: 700, lineHeight: 1 }}>{d.getDate()}</span>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: c.ink }}>{d.toLocaleDateString('en-US', { weekday: 'long' })} · {d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</div>
                      <div style={{ fontSize: 12.5, color: c.muted, marginTop: 1 }}>{relative(iso)}{i === 0 ? ' · next' : ''}</div>
                    </div>
                    <span style={{ fontSize: 11.5, fontWeight: 600, color: c.slate }}>{schedule.scan_type === 'deep' ? 'Deep' : 'Fast'}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div style={{ ...card(isDark), padding: '18px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }}>
            <div>
              <div style={sectionTitle}>Scan activity</div>
              <div style={sectionSub}>Every completed scan in the last 12 weeks.</div>
            </div>
            <span style={{ fontSize: 12.5, color: c.muted }}>{scans.length} total</span>
          </div>
          <ActivityGrid scans={scans} c={c} isDark={isDark} />
          {scans.length === 0 ? (
            <div style={{ padding: '18px 8px', textAlign: 'center', color: c.muted, fontSize: 13.5 }}>No completed scans yet.</div>
          ) : (
            <div style={{ marginTop: 14, borderTop: `1px solid ${c.line}` }}>
              {scans.slice(0, 5).map((s: any, i: number) => {
                const prev = scans[i + 1];
                const delta = prev && s.overall_score != null && prev.overall_score != null ? Math.round(s.overall_score - prev.overall_score) : null;
                return (
                  <div key={s.id || s.scan_id} role="button" onClick={() => navigate(`/scans/${s.id || s.scan_id}/results`)}
                    style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 4px', borderBottom: `1px solid ${c.line}`, cursor: 'pointer' }}
                    onMouseEnter={e => (e.currentTarget.style.background = c.surface2)} onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 600, color: c.ink }}>{fmtDay(s.completed_at || s.created_at)}</div>
                      <div style={{ fontSize: 12, color: c.muted }}>{(s.scan_type || 'fast') === 'deep' ? 'Deep scan' : 'Fast scan'}</div>
                    </div>
                    {s.overall_score != null && (
                      <span style={{ fontSize: 15, fontWeight: 700, color: scoreColor(s.overall_score), fontVariantNumeric: 'tabular-nums' }}>{Math.round(s.overall_score)}</span>
                    )}
                    <span style={{ width: 44, textAlign: 'right', fontSize: 12.5, fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: delta == null || delta === 0 ? c.muted : delta > 0 ? '#16A34A' : '#DC2626' }}>
                      {delta == null ? '' : delta === 0 ? '±0' : `${delta > 0 ? '+' : ''}${delta}`}
                    </span>
                    <span style={{ color: c.muted, display: 'flex' }}><Icon d={I.arrow} size={13} /></span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* History */}
      {history.length > 0 && (
        <div style={{ ...card(isDark), padding: '18px 20px' }}>
          <div style={sectionTitle}>Schedule changes</div>
          <div style={sectionSub}>Who changed this vendor's schedule, and when.</div>
          <div style={{ marginTop: 12 }}>
            {history.map((h: any) => (
              <div key={h.id} style={{ display: 'flex', gap: 12, padding: '10px 0', borderTop: `1px solid ${c.line}` }}>
                <span style={{ color: c.accent, marginTop: 2 }}><Icon d={I.clock} /></span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, color: c.ink }}>{h.details || h.action}</div>
                  <div style={{ fontSize: 12, color: c.muted, marginTop: 2 }}>{h.user_name || 'Someone'} · {relative(h.created_at)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {editorOpen && (
        <ScheduleEditor
          isDark={isDark} c={c} vendorName={vendor.name} recommended={rec}
          initial={toDraft(schedule, editorSeed || {})} isNew={!schedule} saving={save.isPending}
          note={followsDefault ? `${vendor.name} follows your default schedule. Saving here gives it its own schedule, so later changes to the default won't affect it.` : undefined}
          onClose={() => setEditorOpen(false)}
          onSave={async (draft) => {
            try {
              await save.mutateAsync(draft);
              setEditorOpen(false);
              setToast({ text: schedule ? 'Schedule updated.' : `${vendor.name} will now be scanned automatically.` });
            } catch (e) { setToast({ text: apiError(e, 'Could not save the schedule.'), error: true }); }
          }}
        />
      )}

      {confirmRemove && (
        <div onMouseDown={e => { if (e.target === e.currentTarget && !remove.isPending) setConfirmRemove(false); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.5)', zIndex: 1400, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ width: '100%', maxWidth: 460, background: c.surface, border: `1px solid ${c.line}`, borderRadius: 14, overflow: 'hidden', fontFamily: sans, animation: 'vsPop .16s ease-out' }}>
            <div style={{ padding: '22px 24px' }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: c.ink }}>{defaultSchedule?.is_active ? 'Use the default schedule?' : 'Remove this schedule?'}</div>
              <div style={{ fontSize: 14, color: c.slate, marginTop: 8, lineHeight: 1.55 }}>
                {defaultSchedule?.is_active
                  ? `${vendor.name} will drop its own schedule and follow your default instead: ${describe(defaultSchedule)}, ${defaultSchedule.scan_type === 'deep' ? 'deep' : 'fast'} scan.`
                  : `${vendor.name} will stop being scanned automatically, because your default schedule is off. To stop scans for a while instead, pause the schedule.`}
              </div>
            </div>
            <div style={{ padding: '14px 24px', borderTop: `1px solid ${c.line}`, background: c.surface2, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setConfirmRemove(false)} disabled={remove.isPending} style={topBtn(false, isDark)}>Cancel</button>
              <button onClick={doRemove} disabled={remove.isPending} style={{ ...topBtn(true, isDark), background: '#DC2626', borderColor: '#DC2626', color: '#fff', opacity: remove.isPending ? 0.6 : 1 }}>
                {remove.isPending ? 'Saving…' : defaultSchedule?.is_active ? 'Use default schedule' : 'Remove schedule'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 1500, display: 'flex', alignItems: 'center', gap: 12,
          padding: '12px 18px', borderRadius: 10, fontSize: 14, fontFamily: sans, animation: 'vsToast .18s ease-out',
          background: toast.error ? '#DC2626' : (isDark ? '#e5e7eb' : '#0F172A'), color: toast.error ? '#fff' : (isDark ? '#0F172A' : '#fff'),
        }}>
          {!toast.error && <Icon d={I.check} />}{toast.text}
          {toast.link && <button onClick={() => navigate(toast.link!)} style={{ background: 'none', border: 'none', color: 'inherit', fontWeight: 700, textDecoration: 'underline', cursor: 'pointer', fontFamily: sans, fontSize: 14, padding: 0 }}>View progress</button>}
        </div>
      )}
    </div>
  );
}

/* ── small pieces ──────────────────────────────────────────────────── */
function Stat({ label, value, hint, c }: { label: string; value: string; hint?: string; c: Record<string, string> }) {
  return (
    <div style={{ padding: '10px 12px', borderRadius: 9, background: c.surface, border: `1px solid ${c.line}` }}>
      <div style={{ fontSize: 11.5, fontWeight: 600, color: c.slate }}>{label}</div>
      <div style={{ fontSize: 14.5, fontWeight: 700, color: c.ink, marginTop: 4 }}>{value}</div>
      {hint && <div style={{ fontSize: 11.5, color: c.muted, marginTop: 1 }}>{hint}</div>}
    </div>
  );
}

/* 12-week calendar of completed scans (one column per week, Monday at the top). */
function ActivityGrid({ scans, c, isDark }: { scans: any[]; c: Record<string, string>; isDark: boolean }) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const mondayIndex = (today.getDay() + 6) % 7;
  const start = new Date(today); start.setDate(today.getDate() - mondayIndex - 7 * 11);
  const byDay = new Map<string, number>();
  scans.forEach(s => {
    const d = new Date(s.completed_at || s.created_at); d.setHours(0, 0, 0, 0);
    const k = d.toDateString(); byDay.set(k, (byDay.get(k) || 0) + 1);
  });
  const weeks = Array.from({ length: 12 }, (_, w) => Array.from({ length: 7 }, (_, d) => {
    const day = new Date(start); day.setDate(start.getDate() + w * 7 + d); return day;
  }));
  const empty = isDark ? '#1a1a1a' : '#EEF2F6';
  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ display: 'flex', gap: 4 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginRight: 4 }}>
          {['M', '', 'W', '', 'F', '', 'S'].map((l, i) => <span key={i} style={{ height: 13, fontSize: 9.5, color: c.muted, lineHeight: '13px' }}>{l}</span>)}
        </div>
        {weeks.map((week, wi) => (
          <div key={wi} style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
            {week.map(day => {
              const n = byDay.get(day.toDateString()) || 0;
              const future = day > today;
              return (
                <span key={day.toISOString()} title={future ? '' : `${day.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}: ${n ? `${n} scan${n === 1 ? '' : 's'}` : 'no scans'}`}
                  style={{ height: 13, borderRadius: 3, background: future ? 'transparent' : n ? (n > 1 ? '#15803D' : '#22C55E') : empty, border: future ? `1px dashed ${c.line}` : 'none' }} />
              );
            })}
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: c.muted, marginTop: 6 }}>
        <span>{start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 10, height: 10, borderRadius: 2, background: empty }} /> none
          <span style={{ width: 10, height: 10, borderRadius: 2, background: '#22C55E', marginLeft: 6 }} /> 1
          <span style={{ width: 10, height: 10, borderRadius: 2, background: '#15803D', marginLeft: 6 }} /> 2+
        </span>
        <span>Today</span>
      </div>
    </div>
  );
}

/* ── Editor drawer ─────────────────────────────────────────────────── */
export function ScheduleEditor({ isDark, c, vendorName, recommended, initial, isNew, saving, onClose, onSave,
  title, note, hideActive, activeCopy, saveLabel }: {
  isDark: boolean; c: Record<string, string>; vendorName: string;
  recommended?: { label: string; reason: string; draft: Partial<Draft> } | null;
  initial: Draft; isNew: boolean; saving: boolean; onClose: () => void; onSave: (d: Draft) => void;
  title?: string; note?: string; hideActive?: boolean;
  activeCopy?: { on: string; off: string; hint: string }; saveLabel?: string;
}) {
  const [draft, setDraft] = useState<Draft>(initial);
  const [preview, setPreview] = useState<string[]>([]);
  const [previewError, setPreviewError] = useState('');
  const set = (patch: Partial<Draft>) => setDraft(d => ({ ...d, ...patch }));
  const seq = useRef(0);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !saving) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, saving]);

  // Live preview from the server's own calculation, so what's shown is exactly what will run.
  useEffect(() => {
    const id = ++seq.current;
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.post('/v1/monitoring/schedule/preview', {
          frequency: draft.frequency, day_of_week: draft.day_of_week, day_of_month: draft.day_of_month,
          interval_days: draft.interval_days, time: draft.time,
        });
        if (id === seq.current) { setPreview((data.upcoming_runs || []).slice(0, 3)); setPreviewError(''); }
      } catch (e: any) {
        if (id === seq.current) {
          setPreview([]);
          const d = e?.response?.data?.detail;
          setPreviewError(typeof d === 'string' ? d : (d && typeof d === 'object' && typeof d.message === 'string') ? d.message : 'Could not preview this schedule.');
        }
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [draft.frequency, draft.day_of_week, draft.day_of_month, draft.interval_days, draft.time]);

  const recFreq = recommended?.draft.frequency;
  const recInterval = recommended?.draft.interval_days;
  const label: React.CSSProperties = { fontSize: 12, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: c.slate, marginBottom: 10 };
  const optionCard = (on: boolean): React.CSSProperties => ({
    textAlign: 'left', padding: '12px 14px', borderRadius: 10, cursor: 'pointer', fontFamily: sans, position: 'relative',
    border: `1.5px solid ${on ? c.accent : c.line}`, background: on ? c.accentBg : c.surface,
  });
  const chip = (on: boolean): React.CSSProperties => ({
    padding: '8px 0', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: sans, textAlign: 'center',
    border: `1px solid ${on ? c.accent : c.line}`, background: on ? c.accent : c.surface, color: on ? '#fff' : c.ink,
  });
  const input: React.CSSProperties = {
    padding: '10px 12px', fontSize: 14, fontFamily: sans, color: c.ink, background: c.surface,
    border: `1px solid ${c.line}`, borderRadius: 8, outline: 'none',
  };

  return (
    <>
      <div onClick={() => !saving && onClose()} style={{ position: 'fixed', top: 64, left: 0, right: 0, bottom: 0, background: 'rgba(15,23,42,.3)', zIndex: 1200 }} />
      <aside style={{
        position: 'fixed', top: 64, right: 0, bottom: 0, width: 600, maxWidth: '100%', zIndex: 1201, display: 'flex', flexDirection: 'column',
        background: c.surface, borderLeft: `1px solid ${c.line}`, fontFamily: sans, animation: 'vsSlide .18s ease-out',
      }}>
        <div style={{ padding: '22px 26px 18px', borderBottom: `1px solid ${c.line}`, display: 'flex', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 700, color: c.ink }}>{title || (isNew ? 'Set up automatic scans' : 'Edit schedule')}</div>
            <div style={{ fontSize: 13.5, color: c.slate, marginTop: 3 }}>{vendorName}</div>
          </div>
          <button onClick={onClose} aria-label="Close" style={{ background: c.surface2, border: `1px solid ${c.line}`, color: c.slate, cursor: 'pointer', width: 32, height: 32, borderRadius: 8, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Icon d={I.close} />
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '22px 26px', display: 'flex', flexDirection: 'column', gap: 26 }}>
          {note && (
            <div style={{ padding: '11px 14px', borderRadius: 10, background: c.accentBg, color: c.ink, fontSize: 13, lineHeight: 1.5, border: `1px solid ${isDark ? '#1e3a8a' : T.blueBdr}` }}>{note}</div>
          )}
          <section>
            <div style={label}>How often</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 8 }}>
              {CADENCES.map(opt => {
                const on = draft.frequency === opt.value;
                const isRec = opt.value === recFreq;
                return (
                  <button key={opt.value} onClick={() => set({ frequency: opt.value, ...(opt.value === 'custom' && isRec && recInterval ? { interval_days: recInterval } : {}) })} style={optionCard(on)}>
                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, fontSize: 14, fontWeight: 700, color: on ? c.accent : c.ink }}>
                      {opt.label}
                      {isRec && <span style={{ fontSize: 10, fontWeight: 700, color: '#16A34A', background: `#16A34A${isDark ? '22' : '14'}`, borderRadius: 99, padding: '1px 7px', whiteSpace: 'nowrap' }}>Recommended</span>}
                    </span>
                    <span style={{ display: 'block', fontSize: 12, color: c.muted, marginTop: 2 }}>{opt.value === 'custom' && isRec && recInterval ? `Every ${recInterval} days` : opt.hint}</span>
                  </button>
                );
              })}
            </div>
            {recommended && <div style={{ fontSize: 12.5, color: c.muted, marginTop: 8 }}>Recommended: {recommended.label.toLowerCase()} — {recommended.reason.toLowerCase()}.</div>}
          </section>

          {draft.frequency === 'weekly' && (
            <section>
              <div style={label}>On</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6 }}>
                {DAYS.map((d, i) => (
                  <button key={d} onClick={() => set({ day_of_week: i + 1 })} style={chip(draft.day_of_week === i + 1)}>{d.slice(0, 3)}</button>
                ))}
              </div>
            </section>
          )}

          {(draft.frequency === 'monthly' || draft.frequency === 'quarterly') && (
            <section>
              <div style={label}>Day of the month</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6 }}>
                {Array.from({ length: 28 }, (_, i) => i + 1).map(n => (
                  <button key={n} onClick={() => set({ day_of_month: n })} style={chip(draft.day_of_month === n)}>{n}</button>
                ))}
              </div>
              <div style={{ fontSize: 12, color: c.muted, marginTop: 8 }}>
                Up to the 28th, so it exists in every month.{draft.frequency === 'quarterly' ? ' Runs in January, April, July and October.' : ''}
              </div>
            </section>
          )}

          {draft.frequency === 'custom' && (
            <section>
              <div style={label}>Every</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <input type="number" min={1} max={365} value={draft.interval_days} onChange={e => set({ interval_days: Math.min(365, Math.max(1, Number(e.target.value) || 1)) })} style={{ ...input, width: 90 }} />
                <span style={{ fontSize: 14, color: c.ink }}>days</span>
                <div style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
                  {[3, 14, 45, 60].map(n => (
                    <button key={n} onClick={() => set({ interval_days: n })} style={{ ...chip(draft.interval_days === n), padding: '7px 12px' }}>{n}d</button>
                  ))}
                </div>
              </div>
            </section>
          )}

          <section>
            <div style={label}>At</div>
            <input type="time" value={draft.time} onChange={e => set({ time: e.target.value || '09:00' })} style={{ ...input, width: 160 }} />
            <div style={{ fontSize: 12, color: c.muted, marginTop: 6 }}>Server time.</div>
          </section>

          <section>
            <div style={label}>Scan depth</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {DEPTHS.map(d => {
                const on = draft.scan_type === d.value;
                return (
                  <button key={d.value} onClick={() => set({ scan_type: d.value })} style={{ ...optionCard(on), display: 'flex', gap: 10 }}>
                    <span style={{ color: on ? c.accent : c.slate, marginTop: 1 }}><Icon d={d.value === 'deep' ? I.layers : I.bolt} size={16} /></span>
                    <span>
                      <span style={{ display: 'block', fontSize: 14, fontWeight: 700, color: on ? c.accent : c.ink }}>{d.label}</span>
                      <span style={{ display: 'block', fontSize: 12, color: c.muted, marginTop: 3, lineHeight: 1.4 }}>{d.hint}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </section>

          {!hideActive && <section role="button" onClick={() => set({ is_active: draft.is_active ? 0 : 1 })}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '14px 16px', borderRadius: 10, border: `1px solid ${c.line}`, cursor: 'pointer' }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: c.ink }}>{draft.is_active ? (activeCopy?.on || 'Schedule is on') : (activeCopy?.off || 'Schedule is paused')}</div>
              <div style={{ fontSize: 12.5, color: c.slate, marginTop: 2 }}>{activeCopy?.hint || "A paused schedule keeps its settings but doesn't run."}</div>
            </div>
            <span style={{ width: 38, height: 22, borderRadius: 99, background: draft.is_active ? '#16A34A' : (isDark ? '#333' : T.faint), position: 'relative', flexShrink: 0 }}>
              <span style={{ position: 'absolute', top: 2, left: draft.is_active ? 18 : 2, width: 18, height: 18, borderRadius: 99, background: '#fff', transition: 'left .15s' }} />
            </span>
          </section>}

          <section style={{ padding: '16px 18px', borderRadius: 12, background: c.surface2, border: `1px solid ${c.line}` }}>
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: c.slate }}>Summary</div>
            <div style={{ fontSize: 17, fontWeight: 700, color: c.ink, marginTop: 6 }}>
              {describe(draft)} · {draft.scan_type === 'deep' ? 'deep scan' : 'fast scan'}
            </div>
            {previewError ? (
              <div style={{ fontSize: 13, color: '#DC2626', marginTop: 8 }}>{previewError}</div>
            ) : draft.is_active || hideActive ? (
              <div style={{ marginTop: 10, display: 'grid', gap: 6 }}>
                {preview.map((iso, i) => (
                  <div key={iso} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13.5 }}>
                    <span style={{ color: c.ink }}>{i === 0 ? 'First run' : `Then`} · {fmtFull(iso)}</span>
                    <span style={{ color: c.muted }}>{relative(iso)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ fontSize: 13, color: '#D97706', marginTop: 8 }}>Saved as paused — nothing will run until it's turned on.</div>
            )}
          </section>
        </div>

        <div style={{ padding: '14px 26px', borderTop: `1px solid ${c.line}`, background: c.surface2, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} disabled={saving} style={topBtn(false, isDark)}>Cancel</button>
          <button onClick={() => onSave(draft)} disabled={saving || !!previewError} style={{ ...topBtn(true, isDark), opacity: saving || previewError ? 0.6 : 1 }}>
            {saving ? 'Saving…' : saveLabel || (isNew ? 'Start scanning' : 'Save schedule')}
          </button>
        </div>
      </aside>
    </>
  );
}
